<template>
  <div class="border-b border-slate-100 bg-white px-6 py-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
      <!-- Search Input -->
      <div class="md:col-span-4">
        <div class="relative">
          <svg class="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <input
            ref="searchInput"
            :value="searchQuery"
            @input="handleSearchInput"
            type="text"
            :placeholder="placeholderText"
            class="w-full rounded-xl border border-slate-200 bg-slate-50/80 py-3 pl-11 pr-28 text-sm text-slate-900 shadow-inner placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-slate-900/5"
          >
          <div class="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 sm:flex">
            <kbd class="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400">></kbd>
            <kbd class="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400">Player:</kbd>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-3">
      <QuickActions @run="$emit('runCommand', $event)" />
    </div>

    <!-- Quick Date Range Filters and Range Picker -->
    <div class="mt-3 flex flex-wrap items-center gap-2">
      <!-- Quick Range Buttons -->
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="range in quickRanges"
          :key="range.value"
          @click="$emit('update:selectedDateRange', range.value)"
          class="rounded-full px-3 py-1.5 text-xs font-medium transition-all"
          :class="selectedDateRange === range.value ? 
            'bg-slate-900 text-white shadow-sm' : 
            'bg-slate-100 text-slate-600 hover:bg-slate-200'"
        >
          {{ range.label }}
        </button>
      </div>

      <!-- Range Dropdown -->
      <div class="relative range-dropdown">
        <div 
          @click="showRangeDropdown = !showRangeDropdown"
          class="flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
        >
          <i class="fas fa-calendar text-slate-400"></i>
          <span>{{ getRangeText }}</span>
          <i class="fas fa-chevron-down text-[10px] text-slate-400"></i>
        </div>

        <div 
          v-if="showRangeDropdown" 
          class="absolute left-0 top-full z-50 mt-2 grid min-w-[900px] grid-cols-[300px_1fr] gap-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
          @click.stop
        >
          <!-- Left side - Options -->
          <div class="border-r border-slate-100 pr-4">
            <!-- Previous days section -->
            <div class="mb-6">
              <div class="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Previous</div>
              <div 
                v-for="option in rangeOptions.filter(opt => opt.value.startsWith('prev'))"
                :key="option.value"
                @click="selectRange(option.value)"
                class="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-slate-50"
                :class="{ 'bg-rose-50 text-rose-600': selectedDateRange === option.value }"
              >
                <i class="fas fa-check text-xs" v-if="selectedDateRange === option.value"></i>
                <span :class="{ 'ml-4': selectedDateRange !== option.value }">{{ option.label }}</span>
              </div>
            </div>

            <!-- Next days section -->
            <div class="mb-6">
              <div class="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Next</div>
              <div 
                v-for="option in rangeOptions.filter(opt => opt.value.startsWith('next'))"
                :key="option.value"
                @click="selectRange(option.value)"
                class="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-slate-50"
                :class="{ 'bg-rose-50 text-rose-600': selectedDateRange === option.value }"
              >
                <i class="fas fa-check text-xs" v-if="selectedDateRange === option.value"></i>
                <span :class="{ 'ml-4': selectedDateRange !== option.value }">{{ option.label }}</span>
              </div>
            </div>

            <!-- Custom range option -->
            <div>
              <div 
                @click="selectRange('custom')"
                class="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-slate-50"
                :class="{ 'bg-rose-50 text-rose-600': selectedDateRange === 'custom' }"
              >
                <i class="fas fa-check text-xs" v-if="selectedDateRange === 'custom'"></i>
                <span :class="{ 'ml-4': selectedDateRange !== 'custom' }">Custom Range</span>
              </div>
            </div>
          </div>

          <!-- Right side - Calendar -->
          <div v-if="selectedDateRange === 'custom'">
            <DateRangePicker
              v-model="customRange"
              @apply="applyCustomRange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Results Count and Status -->
    <div class="mt-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <p class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {{ searchQuery ? 'Search results' : 'Latest fixtures' }}: {{ filteredCount }} of {{ totalCount }}
        </p>
        <div v-if="searchQuery" class="flex items-center gap-2">
          <div class="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500"></div>
          <span class="text-xs font-medium text-sky-600">Searching...</span>
        </div>
      </div>
      <button 
        @click="$emit('clear')"
        class="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        Clear filters
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, onMounted } from 'vue'
import DateRangePicker from '../DateRangePicker.vue'
import QuickActions from './QuickActions.vue'

const props = defineProps({
  searchQuery: {
    type: String,
    default: ''
  },
  selectedLeague: {
    type: String,
    default: ''
  },
  selectedStatus: {
    type: String,
    default: ''
  },
  selectedDateRange: {
    type: String,
    default: ''
  },
  customRange: {
    type: Object,
    default: () => ({ start: '', end: '' })
  },
  availableLeagues: {
    type: Array,
    default: () => []
  },
  filteredCount: {
    type: Number,
    default: 0
  },
  totalCount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits([
  'update:searchQuery',
  'update:selectedLeague', 
  'update:selectedStatus',
  'update:selectedDateRange',
  'update:customRange',
  'applyCustomRange',
  'clear',
  'runCommand'
])

const showRangeDropdown = ref(false)

const quickRanges = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next3', label: 'Next 3' },
  { value: 'next7', label: 'Next 7' },
]

const rangeOptions = [
  { value: 'prev7', label: 'Last 7 days' },
  { value: 'prev14', label: 'Last 14 days' },
  { value: 'prev30', label: 'Last 30 days' },
  { value: 'prev90', label: 'Last 3 months' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'next14', label: 'Next 14 days' },
  { value: 'next30', label: 'Next 30 days' },
  { value: 'custom', label: 'Custom Range' }
]

const getRangeText = computed(() => {
  if (props.selectedDateRange === 'custom' && props.customRange.start && props.customRange.end) {
    const startDate = new Date(props.customRange.start)
    const endDate = new Date(props.customRange.end)
    return `${formatDateEst(startDate)} - ${formatDateEst(endDate)}`
  }
  return rangeOptions.find(opt => opt.value === props.selectedDateRange)?.label || quickRanges.find(opt => opt.value === props.selectedDateRange)?.label || 'Select Range'
})

const formatDateEst = (date) => {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const selectRange = (value) => {
  emit('update:selectedDateRange', value)
  if (value !== 'custom') {
    showRangeDropdown.value = false
  }
}

const applyCustomRange = () => {
  if (props.customRange.start && props.customRange.end) {
    emit('applyCustomRange')
    showRangeDropdown.value = false
  }
}

const customRange = computed({
  get: () => props.customRange,
  set: (value) => emit('update:customRange', value)
})

const placeholderText = computed(() => {
  return props.searchQuery
    ? 'Searching...'
    : 'Search fixtures, or type > for quick actions...'
})

const handleSearchInput = (event) => {
  const value = event.target.value
  console.log('🔤 Input event fired, value:', value) // Debug log
  emit('update:searchQuery', value)
}

// Auto-focus the search input when component mounts
const searchInput = ref(null)

onMounted(() => {
  // Focus the search input after a short delay to ensure the popup is fully rendered
  setTimeout(() => {
    if (searchInput.value) {
      searchInput.value.focus()
    }
  }, 100)
})
</script>
